import { useMemo, useState } from 'react';
import { Card, Form, Button, Row, Col, Table, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Loader from '../../Components/Loader';
import Message from '../../Components/Message';
import BackButton from '../../Components/BackButton';
import {
  useAdminApplyTierPricesByCategoryMutation,
  useAdminCreatePricingTierMutation,
  useAdminCreatePartnerCodeMutation,
  useAdminListPartnerCodesQuery,
  useAdminListPricingTiersQuery,
  useAdminUpdatePartnerCodeMutation,
} from '../../slices/partnerCodesApiSlice';
import { useGetProductCategoriesQuery } from '../../slices/productsApiSlice';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const PartnerCodesScreen = () => {
  const [tierId, setTierId] = useState('');
  const [active, setActive] = useState('');
  const [expired, setExpired] = useState('');

  const [newTierId, setNewTierId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [usageLimit, setUsageLimit] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [customCode, setCustomCode] = useState('');
  const [tierName, setTierName] = useState('');
  const [tierDescription, setTierDescription] = useState('');
  const [tierActive, setTierActive] = useState(true);
  const [pricingTierId, setPricingTierId] = useState('');
  const [pricingCategory, setPricingCategory] = useState('');
  const [pricingBasis, setPricingBasis] = useState('base');
  const [pricingSourceTierId, setPricingSourceTierId] = useState('');
  const [pricingInputMode, setPricingInputMode] = useState('percent');
  const [percentOfBase, setPercentOfBase] = useState('100');
  const [priceMultiplier, setPriceMultiplier] = useState('1');
  const [fixedAmount, setFixedAmount] = useState('0');
  const [pricingCurrency, setPricingCurrency] = useState('USD');

  const { data: tiers = [], isLoading: loadingTiers } = useAdminListPricingTiersQuery();
  const { data: categories = [], isLoading: loadingCategories } = useGetProductCategoriesQuery();
  
  const {
    data: partnerCodes = [],
    isLoading,
    error,
    refetch,
  } = useAdminListPartnerCodesQuery(
    { tierId: tierId || undefined, active: active || undefined, expired: expired || undefined },
    { refetchOnMountOrArgChange: true }
  );

  const [createPartnerCode, { isLoading: creating }] = useAdminCreatePartnerCodeMutation();
  const [updatePartnerCode, { isLoading: updating }] = useAdminUpdatePartnerCodeMutation();
  const [createPricingTier, { isLoading: creatingTier }] = useAdminCreatePricingTierMutation();
  const [applyTierPricesByCategory, { isLoading: applyingPrices }] =
    useAdminApplyTierPricesByCategoryMutation();

  const tierMap = useMemo(() => {
    const map = new Map();
    tiers.forEach((tier) => map.set(tier._id, tier));
    return map;
  }, [tiers]);

  const categoryOptions = useMemo(
    () =>
      categories
        .map((category) => (typeof category === 'string' ? category : category?.name))
        .filter(Boolean),
    [categories]
  );

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!newTierId) {
      toast.error('Select a tier for the partner code.');
      return;
    }
    try {
      await createPartnerCode({
        tierId: newTierId,
        expiresAt: expiresAt || undefined,
        usageLimit: usageLimit ? Number(usageLimit) : undefined,
        isActive,
        code: customCode || undefined,
      }).unwrap();
      toast.success('Partner code created.');
      setExpiresAt('');
      setUsageLimit('');
      setIsActive(true);
      setCustomCode('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Unable to create code.');
    }
  };

  const submitTierHandler = async (e) => {
    e.preventDefault();
    if (!tierName.trim()) {
      toast.error('Enter a tier name.');
      return;
    }
    try {
      await createPricingTier({
        name: tierName.trim(),
        description: tierDescription.trim(),
        isActive: tierActive,
      }).unwrap();
      toast.success('Pricing tier created.');
      setTierName('');
      setTierDescription('');
      setTierActive(true);
    } catch (err) {
      toast.error(err?.data?.message || 'Unable to create tier.');
    }
  };

  const submitPricingHandler = async (e) => {
    e.preventDefault();
    if (!pricingTierId) {
      toast.error('Select a tier for pricing.');
      return;
    }
    if (!pricingCategory) {
      toast.error('Select a category to apply pricing.');
      return;
    }
    if (pricingBasis === 'tier' && !pricingSourceTierId) {
      toast.error('Select a source tier to base the new prices on.');
      return;
    }
    const percent = Number(percentOfBase);
    const multiplier = Number(priceMultiplier);
    const fixed = Number(fixedAmount);

    if (pricingInputMode === 'percent') {
      if (!Number.isFinite(percent) || percent <= 0) {
        toast.error('Enter a valid percent of base price.');
        return;
      }
    } else if (pricingInputMode === 'multiplier') {
      if (!Number.isFinite(multiplier) || multiplier <= 0) {
        toast.error('Enter a valid multiplier (example: 1.1).');
        return;
      }
    } else if (!Number.isFinite(fixed) || fixed < 0) {
      toast.error('Enter a valid fixed amount.');
      return;
    }

    try {
      const response = await applyTierPricesByCategory({
        tierId: pricingTierId,
        category: pricingCategory,
        basis: pricingBasis,
        sourceTierId: pricingBasis === 'tier' ? pricingSourceTierId : undefined,
        percentOfBase: pricingInputMode === 'percent' ? percent : undefined,
        multiplier: pricingInputMode === 'multiplier' ? multiplier : undefined,
        fixedAmount: pricingInputMode === 'fixed' ? fixed : undefined,
        currency: pricingCurrency,
      }).unwrap();
      const modeText =
        response.pricingMode === 'fixed'
          ? `${response.fixedAmount} ${pricingCurrency}`
          : `${response.multiplierPercent}% (${response.multiplier}x)`;
      const basisText =
        response.basis === 'tier' ? ` of ${response.sourceTier?.name || 'source tier'}'s price` : ' of base price';
      const skippedText = response.skippedCount
        ? ` Skipped ${response.skippedCount} product(s) missing a ${response.sourceTier?.name || 'source tier'} price.`
        : '';
      toast.success(
        `Applied ${modeText}${basisText} pricing to ${response.appliedCount} products. Overwrote ${response.overwrittenCount} existing rows, created ${response.createdCount}.${skippedText}`
      );
    } catch (err) {
      toast.error(err?.data?.message || 'Unable to apply tier prices.');
    }
  };

  const toggleActive = async (code) => {
    try {
      await updatePartnerCode({ id: code._id, isActive: !code.isActive }).unwrap();
      toast.success('Partner code updated.');
    } catch (err) {
      toast.error(err?.data?.message || 'Update failed.');
    }
  };

  return (
    <>
      <BackButton />
      <Row className="g-4">
        <Col lg={4}>
          <Card className="shadow-sm">
            <Card.Body>
              <h4 className="mb-3">Create Pricing Tier</h4>
              <Form onSubmit={submitTierHandler} className="mb-4">
                <Form.Group className="mb-3" controlId="tierName">
                  <Form.Label>Tier Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Tier1"
                    value={tierName}
                    onChange={(e) => setTierName(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="tierDescription">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Optional notes about this tier"
                    value={tierDescription}
                    onChange={(e) => setTierDescription(e.target.value)}
                  />
                </Form.Group>
                <Form.Check
                  type="switch"
                  id="tierActive"
                  label="Active"
                  checked={tierActive}
                  onChange={(e) => setTierActive(e.target.checked)}
                />
                <Button type="submit" className="mt-3" disabled={creatingTier}>
                  {creatingTier ? 'Creating...' : 'Create Tier'}
                </Button>
              </Form>

              <h4 className="mb-3">Apply Tier Pricing by Category</h4>
              {loadingCategories ? (
                <Loader />
              ) : (
                <Form onSubmit={submitPricingHandler} className="mb-4">
                  <Form.Group className="mb-3" controlId="pricingTier">
                    <Form.Label>Pricing Tier</Form.Label>
                    <Form.Select
                      value={pricingTierId}
                      onChange={(e) => setPricingTierId(e.target.value)}
                    >
                      <option value="">Select a tier</option>
                      {tiers.map((tier) => (
                        <option key={tier._id} value={tier._id}>
                          {tier.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="pricingCategory">
                    <Form.Label>Category</Form.Label>
                    <Form.Select
                      value={pricingCategory}
                      onChange={(e) => setPricingCategory(e.target.value)}
                    >
                      <option value="">Select a category</option>
                      {categoryOptions.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Row>
                    <Col sm={6}>
                      <Form.Group className="mb-3" controlId="pricingBasis">
                        <Form.Label>Base Price On</Form.Label>
                        <Form.Select
                          value={pricingBasis}
                          onChange={(e) => {
                            setPricingBasis(e.target.value);
                            if (e.target.value !== 'tier') setPricingSourceTierId('');
                          }}
                        >
                          <option value="base">Product's Base Price</option>
                          <option value="tier">Another Tier's Price</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group className="mb-3" controlId="pricingCurrency">
                        <Form.Label>Currency</Form.Label>
                        <Form.Control
                          type="text"
                          value={pricingCurrency}
                          onChange={(e) => setPricingCurrency(e.target.value.toUpperCase())}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  {pricingBasis === 'tier' && (
                    <Form.Group className="mb-3" controlId="pricingSourceTier">
                      <Form.Label>Source Tier</Form.Label>
                      <Form.Select
                        value={pricingSourceTierId}
                        onChange={(e) => setPricingSourceTierId(e.target.value)}
                      >
                        <option value="">Select a tier</option>
                        {tiers.map((tier) => (
                          <option key={tier._id} value={tier._id}>
                            {tier.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        New prices are computed from this tier's saved price per product. Products
                        without a saved price for this tier will be skipped. Pick the same tier as
                        both source and target (above) to discount a tier off its own current price.
                      </Form.Text>
                    </Form.Group>
                  )}
                  <Row>
                    <Col sm={6}>
                      <Form.Group className="mb-3" controlId="pricingInputMode">
                        <Form.Label>Pricing Input</Form.Label>
                        <Form.Select
                          value={pricingInputMode}
                          onChange={(e) => setPricingInputMode(e.target.value)}
                        >
                          <option value="percent">
                            {pricingBasis === 'tier' ? 'Percent of Source Tier Price' : 'Percent of Base Price'}
                          </option>
                          <option value="multiplier">Multiplier (x)</option>
                          <option value="fixed">Fixed Amount</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  {pricingInputMode === 'percent' ? (
                    <Form.Group className="mb-3" controlId="percentOfBase">
                      <Form.Label>
                        {pricingBasis === 'tier' ? 'Percent of Source Tier Price' : 'Percent of Base Price'}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        step="0.01"
                        value={percentOfBase}
                        onChange={(e) => setPercentOfBase(e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        {pricingBasis === 'tier'
                          ? 'Example: 85 = tier price set to 85% of the source tier price (15% off).'
                          : 'Example: 90 = 90% of base price.'}
                      </Form.Text>
                    </Form.Group>
                  ) : pricingInputMode === 'multiplier' ? (
                    <Form.Group className="mb-3" controlId="priceMultiplier">
                      <Form.Label>Multiplier (x)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={priceMultiplier}
                        onChange={(e) => setPriceMultiplier(e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        {pricingBasis === 'tier'
                          ? 'Multiplies the source tier price. Example: 0.95 = 5% off the source tier price.'
                          : 'Example: 1.10 = increase all prices by 10%, 0.85 = 15% off.'}
                      </Form.Text>
                    </Form.Group>
                  ) : (
                    <Form.Group className="mb-3" controlId="fixedAmount">
                      <Form.Label>Fixed Amount</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={fixedAmount}
                        onChange={(e) => setFixedAmount(e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        Sets every product in this category to the same tier price.
                      </Form.Text>
                    </Form.Group>
                  )}
                  <Button type="submit" className="mt-2" disabled={applyingPrices}>
                    {applyingPrices ? 'Applying...' : 'Apply Pricing'}
                  </Button>
                </Form>
              )}

              <h4>Create Partner Code</h4>
              {loadingTiers ? (
                <Loader />
              ) : (
                <Form onSubmit={submitHandler}>
                  <Form.Group className="mb-3" controlId="tierId">
                    <Form.Label>Pricing Tier</Form.Label>
                    <Form.Select value={newTierId} onChange={(e) => setNewTierId(e.target.value)}>
                      <option value="">Select a tier</option>
                      {tiers.map((tier) => (
                        <option key={tier._id} value={tier._id}>
                          {tier.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="customCode">
                    <Form.Label>Custom Code (optional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Leave blank for auto-generated"
                      value={customCode}
                      onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                    />
                  </Form.Group>

                  <Row>
                    <Col sm={6}>
                      <Form.Group className="mb-3" controlId="expiresAt">
                        <Form.Label>Expiry Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={expiresAt}
                          onChange={(e) => setExpiresAt(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group className="mb-3" controlId="usageLimit">
                        <Form.Label>Usage Limit</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={usageLimit}
                          onChange={(e) => setUsageLimit(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Check
                    type="switch"
                    id="isActive"
                    label="Active"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />

                  <Button type="submit" className="mt-3" disabled={creating}>
                    {creating ? 'Creating...' : 'Create Code'}
                  </Button>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={8}>
          <Card className="shadow-sm">
            <Card.Body>
              <h4>Partner Codes</h4>
              <Row className="g-2 mb-3">
                <Col md={4}>
                  <Form.Select value={tierId} onChange={(e) => setTierId(e.target.value)}>
                    <option value="">All tiers</option>
                    {tiers.map((tier) => (
                      <option key={tier._id} value={tier._id}>
                        {tier.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Select value={active} onChange={(e) => setActive(e.target.value)}>
                    <option value="">Active + inactive</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Select value={expired} onChange={(e) => setExpired(e.target.value)}>
                    <option value="">Expired + valid</option>
                    <option value="false">Not expired</option>
                    <option value="true">Expired</option>
                  </Form.Select>
                </Col>
              </Row>

              {isLoading ? (
                <Loader />
              ) : error ? (
                <Message variant="danger">{error?.data?.message || error.error}</Message>
              ) : (
                <Table responsive hover className="table-sm">
                  <thead>
                    <tr>
                      <th>CODE</th>
                      <th>TIER</th>
                      <th>USAGE</th>
                      <th>EXPIRES</th>
                      <th>STATUS</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnerCodes.map((code) => (
                      <tr key={code._id}>
                        <td>{code.code}</td>
                        <td>{code.tier?.name || tierMap.get(code.tier)?.name || '—'}</td>
                        <td>
                          {code.usageCount}
                          {code.usageLimit ? ` / ${code.usageLimit}` : ''}
                        </td>
                        <td>{formatDate(code.expiresAt)}</td>
                        <td>
                          {code.isActive ? (
                            <Badge bg="success">Active</Badge>
                          ) : (
                            <Badge bg="secondary">Inactive</Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant={code.isActive ? 'outline-secondary' : 'outline-success'}
                            disabled={updating}
                            onClick={() => toggleActive(code)}
                          >
                            {code.isActive ? 'Deactivate' : 'Activate'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PartnerCodesScreen;
